import { useState } from 'react';
import { Smartphone, ExternalLink, MessageSquare, ChevronLeft } from 'lucide-react';
import { clsx } from 'clsx';
import { Card } from '@/shared/ui/Card';
import patterns from '@/shared/ui/styles/patterns.module.css';
import { PracticeHeader, OptionBtn, StarPicker } from './CapturePagePrimitives';
import captureStyles from '../pages/ReviewCapturePage.module.css';
import {
  REVIEW_ASSIST_PRIMARY_THEMES,
  REVIEW_ASSIST_SUPPORTING_DETAILS,
  REVIEW_ASSIST_REASONS,
  MAX_SUPPORTING_DETAILS,
  MAX_PEOPLE,
  PRIMARY_THEME_PROMPT,
  SUPPORTING_DETAIL_PROMPT,
  REASON_PROMPT,
  PEOPLE_PROMPT,
  OWN_WORDS_PROMPT,
  OWN_WORDS_SUBTITLE,
  OWN_WORDS_PLACEHOLDER,
  OWN_WORDS_PRIVACY_NOTE,
  OWN_WORDS_MAX_LENGTH,
  normalizePeopleWriteIns,
} from '../captureQuestions';
import type { ReviewSupportingDetail } from '../captureQuestions';
import type { InviteResolution } from '../api/captureApi';
import styles from './CaptureTab.module.css';

const DEMO_RESOLUTION: InviteResolution = {
  practiceName: 'Austin Dental Group',
  locationCity: 'Austin, TX',
  logoUrl: null,
  logoOnDark: false,
  gbpDeepLink: '#',
  people: [
    { name: 'Dr. Patel', roleLabel: 'Dentist' },
    { name: 'Jamie R.', roleLabel: 'Hygienist' },
  ],
};
// Fixed demo theme so PreviewQ2Screen has a single, consistent set of follow-ups to show.
const DEMO_THEME = REVIEW_ASSIST_PRIMARY_THEMES[0].value;
const DEMO_Q2_OPTIONS = REVIEW_ASSIST_SUPPORTING_DETAILS[DEMO_THEME];
const DEMO_DRAFT =
  'I recently visited and was really impressed by the care throughout my visit. Highly recommend!';

// Mirrors the step dots in CaptureHappyPathFlow (people, reason, q1, q2, ownWords).
const DEMO_STEPS = ['people', 'reason', 'q1', 'q2', 'ownWords'] as const;

function PreviewDots({ current }: { current: (typeof DEMO_STEPS)[number] }) {
  const idx = DEMO_STEPS.indexOf(current);
  return (
    <div className={captureStyles.dots} aria-hidden>
      {DEMO_STEPS.map((s, i) => (
        <span
          key={s}
          className={
            i < idx
              ? captureStyles.dotDone
              : i === idx
                ? captureStyles.dotActive
                : captureStyles.dot
          }
        />
      ))}
    </div>
  );
}

type PreviewNavProps = { onNext: () => void; onBack: () => void };

function PreviewRatingScreen({ onNext }: PreviewNavProps) {
  const [rating, setRating] = useState<number | null>(null);

  return (
    <div className={captureStyles.card}>
      <div className={captureStyles.hero}>Share your experience</div>
      <p className={captureStyles.sub}>Tell us how it went - it'll only take a moment.</p>
      <StarPicker
        value={rating}
        onChange={(n) => {
          setRating(n);
          onNext();
        }}
      />
    </div>
  );
}

function PreviewPeopleScreen({ onNext, onBack }: PreviewNavProps) {
  const [selected, setSelected] = useState<string[]>([]);
  const [writeIns, setWriteIns] = useState<string[]>(['']);

  const typedCount = writeIns.filter((w) => w.trim() !== '').length;
  const selectedCount = selected.length + typedCount;

  return (
    <div className={captureStyles.card}>
      <PreviewDots current="people" />
      <div className={captureStyles.question}>{PEOPLE_PROMPT}</div>
      <div className={captureStyles.options}>
        {DEMO_RESOLUTION.people.map((person) => (
          <OptionBtn
            key={person.name}
            label={person.name}
            selected={selected.includes(person.name)}
            onClick={() =>
              setSelected((s) =>
                s.includes(person.name)
                  ? s.filter((v) => v !== person.name)
                  : selectedCount >= MAX_PEOPLE
                    ? s
                    : [...s, person.name],
              )
            }
            disabled={!selected.includes(person.name) && selectedCount >= MAX_PEOPLE}
          />
        ))}
      </div>
      <div className={captureStyles.contactFields}>
        {writeIns.map((value, i) => (
          <input
            key={i}
            type="text"
            className={captureStyles.contactInput}
            value={value}
            onChange={(e) => {
              const next = [...writeIns];
              next[i] = e.target.value;
              setWriteIns(normalizePeopleWriteIns(next, selected.length));
            }}
            maxLength={60}
            placeholder="Someone else? Type their name"
            aria-label="Add a person by name"
          />
        ))}
      </div>
      <button type="button" className={captureStyles.btnPrimary} onClick={onNext}>
        Next
      </button>
      <button type="button" className={captureStyles.backBtn} onClick={onBack}>
        <ChevronLeft size={16} aria-hidden /> Back
      </button>
    </div>
  );
}

function PreviewReasonScreen({ onNext, onBack }: PreviewNavProps) {
  const [selected, setSelected] = useState<string>('');

  return (
    <div className={captureStyles.card}>
      <PreviewDots current="reason" />
      <div className={captureStyles.question}>{REASON_PROMPT}</div>
      <div className={captureStyles.options}>
        {REVIEW_ASSIST_REASONS.map((reason) => (
          <OptionBtn
            key={reason.value}
            label={reason.label}
            selected={selected === reason.value}
            onClick={() => {
              setSelected(reason.value);
              window.setTimeout(onNext, 250);
            }}
          />
        ))}
      </div>
      <button type="button" className={captureStyles.backBtn} onClick={onBack}>
        <ChevronLeft size={16} aria-hidden /> Back
      </button>
    </div>
  );
}

function PreviewQ1Screen({ onNext, onBack }: PreviewNavProps) {
  const [selected, setSelected] = useState<string>('');

  return (
    <div className={captureStyles.card}>
      <PreviewDots current="q1" />
      <div className={captureStyles.question}>{PRIMARY_THEME_PROMPT}</div>
      <div className={captureStyles.options}>
        {REVIEW_ASSIST_PRIMARY_THEMES.map((theme) => (
          <OptionBtn
            key={theme.value}
            label={theme.label}
            selected={selected === theme.value}
            onClick={() => {
              setSelected(theme.value);
              window.setTimeout(onNext, 250);
            }}
          />
        ))}
      </div>
      <button type="button" className={captureStyles.backBtn} onClick={onBack}>
        <ChevronLeft size={16} aria-hidden /> Back
      </button>
    </div>
  );
}

function PreviewQ2Screen({ onNext, onBack }: PreviewNavProps) {
  const [selected, setSelected] = useState<ReviewSupportingDetail[]>([]);

  return (
    <div className={captureStyles.card}>
      <PreviewDots current="q2" />
      <div className={captureStyles.question}>{SUPPORTING_DETAIL_PROMPT}</div>
      <div className={captureStyles.options}>
        {DEMO_Q2_OPTIONS.map((opt) => (
          <OptionBtn
            key={opt.value}
            label={opt.label}
            selected={selected.includes(opt.value)}
            onClick={() =>
              setSelected((s) =>
                s.includes(opt.value)
                  ? s.filter((v) => v !== opt.value)
                  : s.length >= MAX_SUPPORTING_DETAILS
                    ? s
                    : [...s, opt.value],
              )
            }
            disabled={!selected.includes(opt.value) && selected.length >= MAX_SUPPORTING_DETAILS}
          />
        ))}
      </div>
      <button type="button" className={captureStyles.btnPrimary} onClick={onNext}>
        Next
      </button>
      <button type="button" className={captureStyles.backBtn} onClick={onBack}>
        <ChevronLeft size={16} aria-hidden /> Back
      </button>
    </div>
  );
}

function PreviewOwnWordsScreen({ onNext, onBack }: PreviewNavProps) {
  const [value, setValue] = useState('');

  return (
    <div className={captureStyles.card}>
      <PreviewDots current="ownWords" />
      <div className={captureStyles.question}>{OWN_WORDS_PROMPT}</div>
      <p className={captureStyles.sub}>{OWN_WORDS_SUBTITLE}</p>
      <textarea
        className={captureStyles.privateArea}
        value={value}
        onChange={(e) => setValue(e.target.value.slice(0, OWN_WORDS_MAX_LENGTH))}
        rows={3}
        maxLength={OWN_WORDS_MAX_LENGTH}
        placeholder={OWN_WORDS_PLACEHOLDER}
      />
      <p className={captureStyles.charCounter}>
        {value.length}/{OWN_WORDS_MAX_LENGTH}
      </p>
      <p className={captureStyles.sub}>{OWN_WORDS_PRIVACY_NOTE}</p>
      <button type="button" className={captureStyles.btnPrimary} onClick={onNext}>
        {value.trim() ? 'Continue' : 'Skip for now'}
      </button>
      <button type="button" className={captureStyles.backBtn} onClick={onBack}>
        <ChevronLeft size={16} aria-hidden /> Back
      </button>
    </div>
  );
}

function PreviewExpectationScreen({ onNext, onBack }: PreviewNavProps) {
  return (
    <div className={captureStyles.card}>
      <div className={captureStyles.question}>One more step after this</div>
      <p className={captureStyles.sub}>
        Next you&apos;ll get your review to look over and edit. When you&apos;re ready to post it,
        we&apos;ll send you to Google — here&apos;s what that looks like once you&apos;re there:
      </p>
      <ol className={captureStyles.postSteps}>
        <li className={captureStyles.postStep}>
          <span className={captureStyles.postStepNum}>1</span> Tap the stars ★★★★★
        </li>
        <li className={captureStyles.postStep}>
          <span className={captureStyles.postStepNum}>2</span> Press and hold inside the big text
          box
        </li>
        <li className={captureStyles.postStep}>
          <span className={captureStyles.postStepNum}>3</span> Tap <strong>Paste</strong>
        </li>
      </ol>
      <p className={captureStyles.copiedHintAlert}>
        Your review isn&apos;t posted until you finish those steps on Google.
      </p>
      <button type="button" className={captureStyles.btnPrimary} onClick={onNext}>
        Got it — show me my review
      </button>
      <button type="button" className={captureStyles.backBtn} onClick={onBack}>
        <ChevronLeft size={16} aria-hidden /> Back
      </button>
    </div>
  );
}

function PreviewShareScreen({ onNext, onBack }: PreviewNavProps) {
  return (
    <div className={captureStyles.card}>
      <div className={captureStyles.question}>Your review</div>
      <p className={captureStyles.sub}>You can edit this generated review or start fresh.</p>
      <div className={captureStyles.draftArea}>{DEMO_DRAFT}</div>
      <div className={captureStyles.googleBtnWrap}>
        <button type="button" className={captureStyles.ctaGoogle}>
          <ExternalLink size={16} aria-hidden /> Open Google to paste the review
        </button>
        <p className={captureStyles.copiedHintAlert}>
          We copy the review for you — just paste it in when Google opens.
        </p>
      </div>
      <div className={captureStyles.shareDivider}>or</div>
      <button type="button" className={captureStyles.btnPrivate} onClick={onNext}>
        <MessageSquare size={16} aria-hidden /> Tell the office privately
      </button>
      <button type="button" className={captureStyles.backBtn} onClick={onBack}>
        <ChevronLeft size={16} aria-hidden /> Back
      </button>
    </div>
  );
}

function PreviewPrivateScreen({ onBack }: PreviewNavProps) {
  return (
    <div className={captureStyles.card}>
      <div className={captureStyles.progress}>
        <div className={clsx(captureStyles.progressBar, styles.progressBarFull)} />
      </div>
      <div className={captureStyles.draftHeader}>
        <div className={captureStyles.question}>Tell the office privately</div>
        <span className={captureStyles.clearDraftBtn}>Write my own</span>
      </div>
      <div className={captureStyles.privateArea}>{DEMO_DRAFT}</div>
      <div className={captureStyles.contactFields}>
        <div className={captureStyles.contactInput}>Your name (optional)</div>
        <div className={captureStyles.contactInput}>Phone or email (optional)</div>
      </div>
      <button type="button" className={captureStyles.btnSecondary}>
        Send privately
      </button>
      <button type="button" className={captureStyles.backBtn} onClick={onBack}>
        <ChevronLeft size={16} aria-hidden /> Back
      </button>
    </div>
  );
}

const PHONE_SCREENS = [
  { key: 'rating', label: 'Rating', Screen: PreviewRatingScreen },
  { key: 'people', label: 'People', Screen: PreviewPeopleScreen },
  { key: 'reason', label: 'Reason', Screen: PreviewReasonScreen },
  { key: 'q1', label: 'Q1', Screen: PreviewQ1Screen },
  { key: 'q2', label: 'Q2', Screen: PreviewQ2Screen },
  { key: 'ownWords', label: 'Your words', Screen: PreviewOwnWordsScreen },
  { key: 'expectation', label: 'What to expect', Screen: PreviewExpectationScreen },
  { key: 'share', label: 'Share', Screen: PreviewShareScreen },
  { key: 'private', label: 'Private', Screen: PreviewPrivateScreen },
] as const;

export function PhonePreview() {
  const [activeIndex, setActiveIndex] = useState(0);
  const { Screen } = PHONE_SCREENS[activeIndex];
  const goNext = () => setActiveIndex((i) => Math.min(i + 1, PHONE_SCREENS.length - 1));
  const goBack = () => setActiveIndex((i) => Math.max(i - 1, 0));

  return (
    <Card padding="lg">
      <div className={patterns.panelTitle}>
        <Smartphone size={16} aria-hidden /> What patients see
      </div>
      <p className={styles.captureDesc}>
        The flow patients experience after scanning your QR code.
      </p>
      <div className={styles.phoneMockupWrap}>
        <div className={styles.phoneMockup} data-theme="light" data-palette="sunset">
          <div className={styles.phoneContentScale}>
            {activeIndex === 0 && <PracticeHeader resolution={DEMO_RESOLUTION} />}
            <Screen onNext={goNext} onBack={goBack} />
          </div>
          <div className={styles.phoneCamera} />
        </div>
        <div className={styles.phoneNavRow}>
          {PHONE_SCREENS.map((s, i) => (
            <button
              key={s.key}
              type="button"
              className={clsx(styles.phoneNavTab, i === activeIndex && styles.phoneNavTabActive)}
              onClick={() => setActiveIndex(i)}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>
    </Card>
  );
}
